import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Building2,
  Receipt,
  CreditCard,
  FileText,
  Download,
  ChevronDown,
  Sun,
  Moon,
  LogOut,
  User,
  Settings,
  RefreshCw,
  ExternalLink,
  Filter,
  Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function OrgView() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  
  // User & Auth
  const [user, setUser] = useState(null);
  const [userOrgs, setUserOrgs] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [preferences, setPreferences] = useState({ theme: "dark" });
  
  // Data
  const [activeTab, setActiveTab] = useState("bills");
  const [stats, setStats] = useState({});
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reports, setReports] = useState([]);
  
  // Pagination
  const [billPage, setBillPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const [reportPage, setReportPage] = useState(1);
  const [billPages, setBillPages] = useState(0);
  const [paymentPages, setPaymentPages] = useState(0);
  const [reportPages, setReportPages] = useState(0);
  
  // Filters
  const [billFilters, setBillFilters] = useState({ vendor: "", billing_id: "" });
  const [hideDuplicates, setHideDuplicates] = useState(true);
  
  // Loading
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("auth_token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, []);

  // Apply theme
  useEffect(() => {
    if (preferences.theme === "light") {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    } else {
      document.documentElement.classList.remove("light");
      document.documentElement.classList.add("dark");
    }
  }, [preferences.theme]);

  // Load user data and orgs
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) {
          navigate("/");
          return;
        }

        // Decode token to get user
        const payload = JSON.parse(atob(token.split(".")[1]));
        setUser(payload);

        // Load preferences
        let userPreferences = { theme: "dark", default_org_id: null };
        const prefsRes = await fetch(`${API_BASE}/api/user/preferences`, {
          headers: getAuthHeaders(),
        });
        if (prefsRes.ok) {
          const prefsData = await prefsRes.json();
          userPreferences = prefsData.preferences || userPreferences;
          setPreferences(userPreferences);
        }

        // Load user's orgs
        const orgsRes = await fetch(`${API_BASE}/api/my-orgs`, {
          headers: getAuthHeaders(),
        });
        if (orgsRes.ok) {
          const orgsData = await orgsRes.json();
          setUserOrgs(orgsData.orgs);
          
          // Set current org
          const targetOrgId = orgId || userPreferences?.default_org_id;
          if (targetOrgId) {
            const found = orgsData.orgs.find(o => o.org.id === targetOrgId);
            if (found) {
              setCurrentOrg(found);
            } else if (orgsData.orgs.length > 0) {
              setCurrentOrg(orgsData.orgs[0]);
              navigate(`/org/${orgsData.orgs[0].org.id}`);
            }
          } else if (orgsData.orgs.length > 0) {
            setCurrentOrg(orgsData.orgs[0]);
            navigate(`/org/${orgsData.orgs[0].org.id}`);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Failed to load user data:", error);
        navigate("/");
      }
    };

    loadUserData();
  }, [orgId, navigate, getAuthHeaders]);

  // Load org data when currentOrg changes
  useEffect(() => {
    if (!currentOrg) return;

    const loadOrgData = async () => {
      try {
        // Load stats
        const statsRes = await fetch(
          `${API_BASE}/api/org-view/${currentOrg.org.id}/stats`,
          { headers: getAuthHeaders() }
        );
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }

        // Load initial bills
        loadBills();
      } catch (error) {
        console.error("Failed to load org data:", error);
      }
    };

    loadOrgData();
  }, [currentOrg, getAuthHeaders]);

  const loadBills = useCallback(async () => {
    if (!currentOrg) return;
    
    const params = new URLSearchParams({
      page: billPage,
      limit: 50,
      hide_duplicates: hideDuplicates,
    });
    if (billFilters.vendor) params.append("vendor", billFilters.vendor);
    if (billFilters.billing_id) params.append("billing_id", billFilters.billing_id);

    const res = await fetch(
      `${API_BASE}/api/org-view/${currentOrg.org.id}/bills?${params}`,
      { headers: getAuthHeaders() }
    );
    if (res.ok) {
      const data = await res.json();
      setBills(data.bills);
      setBillPages(data.pages);
    }
  }, [currentOrg, billPage, billFilters, hideDuplicates, getAuthHeaders]);

  const loadPayments = useCallback(async () => {
    if (!currentOrg) return;
    
    const params = new URLSearchParams({
      page: paymentPage,
      limit: 50,
      hide_duplicates: hideDuplicates,
    });

    const res = await fetch(
      `${API_BASE}/api/org-view/${currentOrg.org.id}/payments?${params}`,
      { headers: getAuthHeaders() }
    );
    if (res.ok) {
      const data = await res.json();
      setPayments(data.payments);
      setPaymentPages(data.pages);
    }
  }, [currentOrg, paymentPage, hideDuplicates, getAuthHeaders]);

  const loadReports = useCallback(async () => {
    if (!currentOrg) return;
    
    const params = new URLSearchParams({
      page: reportPage,
      limit: 50,
      hide_duplicates: hideDuplicates,
    });

    const res = await fetch(
      `${API_BASE}/api/org-view/${currentOrg.org.id}/reports?${params}`,
      { headers: getAuthHeaders() }
    );
    if (res.ok) {
      const data = await res.json();
      setReports(data.reports);
      setReportPages(data.pages);
    }
  }, [currentOrg, reportPage, hideDuplicates, getAuthHeaders]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === "bills") loadBills();
    else if (activeTab === "payments") loadPayments();
    else if (activeTab === "reports") loadReports();
  }, [activeTab, loadBills, loadPayments, loadReports]);

  const toggleTheme = async () => {
    const newTheme = preferences.theme === "dark" ? "light" : "dark";
    setPreferences({ ...preferences, theme: newTheme });
    
    await fetch(`${API_BASE}/api/user/preferences`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify({ theme: newTheme }),
    });
  };

  const switchOrg = (org) => {
    setCurrentOrg(org);
    navigate(`/org/${org.org.id}`);
    setBillPage(1);
    setPaymentPage(1);
    setReportPage(1);
  };

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    navigate("/");
  };

  const downloadFile = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${preferences.theme === 'light' ? 'bg-gray-50' : 'bg-zinc-950'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!currentOrg) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${preferences.theme === 'light' ? 'bg-gray-50 text-gray-900' : 'bg-zinc-950 text-white'}`}>
        <Building2 className="w-16 h-16 mb-4 opacity-20" />
        <h1 className="text-xl font-semibold mb-2">No Organizations</h1>
        <p className="text-gray-500">You are not assigned to any organizations yet.</p>
        <Button onClick={handleLogout} variant="outline" className="mt-4">
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    );
  }

  const bgClass = preferences.theme === 'light' ? 'bg-white' : 'bg-zinc-950';
  const cardBgClass = preferences.theme === 'light' ? 'bg-white border-gray-200 shadow-sm' : 'bg-zinc-900 border-zinc-800';
  const textClass = preferences.theme === 'light' ? 'text-gray-900' : 'text-white';
  const mutedTextClass = preferences.theme === 'light' ? 'text-gray-600' : 'text-zinc-400';
  const tableBgClass = preferences.theme === 'light' ? 'bg-gray-100' : 'bg-zinc-900/50';
  const borderClass = preferences.theme === 'light' ? 'border-gray-200' : 'border-zinc-800';
  const rowTextClass = preferences.theme === 'light' ? 'text-gray-800' : 'text-zinc-200';
  const rowHoverClass = preferences.theme === 'light' ? 'hover:bg-gray-100' : 'hover:bg-zinc-900/50';
  const iconClass = preferences.theme === 'light' ? 'text-gray-700' : 'text-zinc-400';
  const buttonHoverClass = preferences.theme === 'light' ? 'hover:bg-gray-200' : 'hover:bg-zinc-800';
  const statusBadgeClass = preferences.theme === 'light' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-green-900/30 text-green-400';
  const downloadBtnClass = preferences.theme === 'light' ? 'text-gray-700 hover:bg-gray-200 hover:text-gray-900' : 'text-zinc-400 hover:bg-zinc-800 hover:text-white';

  return (
    <div className={`min-h-screen ${bgClass} ${textClass}`} data-testid="org-view">
      {/* Header */}
      <header className={`border-b ${preferences.theme === 'light' ? 'border-gray-200 bg-white' : 'border-zinc-800 bg-zinc-900'} px-4 py-3`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left side - Logo + Org Switcher */}
          <div className="flex items-center gap-4">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src="/billportal-logo.png" alt="BillPortal" className="w-8 h-8" />
              <span className={`font-bold text-lg ${preferences.theme === 'light' ? 'text-gray-900' : 'text-white'}`}>BillPortal</span>
            </div>
            
            {/* Org Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`flex items-center gap-2 ${preferences.theme === 'light' ? 'hover:bg-gray-100' : 'hover:bg-zinc-800'}`} data-testid="org-switcher">
                  <Building2 className="w-5 h-5 text-purple-500" />
                  <span className="font-semibold">{currentOrg.org.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className={cardBgClass}>
                <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {userOrgs.map((orgItem) => (
                  <DropdownMenuItem
                    key={orgItem.org.id}
                    onClick={() => switchOrg(orgItem)}
                    className={currentOrg.org.id === orgItem.org.id ? "bg-purple-500/20" : ""}
                    data-testid={`org-option-${orgItem.org.id}`}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    {orgItem.org.name}
                    <span className={`ml-2 text-xs ${mutedTextClass}`}>({orgItem.role})</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right side - Back to Admin + Theme toggle & User menu */}
          <div className="flex items-center gap-2">
            {/* Org Settings button - visible for super_admin and admin */}
            {(user?.role === "super_admin" || user?.role === "admin") && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate(`/org/${orgId}/settings`)}
                className={`${preferences.theme === 'light' ? 'border-gray-300 hover:bg-gray-100 text-gray-700' : 'border-zinc-700 hover:bg-zinc-800 text-zinc-300'}`}
                data-testid="org-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            )}
            
            {/* Back to Admin button - visible for super_admin and admin */}
            {(user?.role === "super_admin" || user?.role === "admin") && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate("/")}
                className={`${preferences.theme === 'light' ? 'border-gray-300 hover:bg-gray-100 text-gray-700' : 'border-zinc-700 hover:bg-zinc-800 text-zinc-300'}`}
                data-testid="back-to-admin-btn"
              >
                Admin Panel
              </Button>
            )}
            
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme} 
              data-testid="theme-toggle"
              className={preferences.theme === 'light' ? 'hover:bg-gray-100 text-gray-700' : 'hover:bg-zinc-800 text-zinc-300'}
            >
              {preferences.theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`flex items-center gap-2 ${preferences.theme === 'light' ? 'hover:bg-gray-100 text-gray-700' : 'hover:bg-zinc-800 text-zinc-300'}`} data-testid="user-menu">
                  <User className="w-5 h-5" />
                  <span className="hidden md:inline">{user?.name || user?.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={cardBgClass}>
                <DropdownMenuLabel>
                  <div>{user?.name}</div>
                  <div className={`text-xs ${mutedTextClass}`}>{user?.email}</div>
                  <div className={`text-xs ${mutedTextClass}`}>Org Role: {currentOrg.role.replace('org_', '')}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* Back to Admin for super_admin and admin */}
                {(user?.role === "super_admin" || user?.role === "admin") && (
                  <DropdownMenuItem onClick={() => navigate("/")} className="text-purple-400">
                    <Settings className="w-4 h-4 mr-2" />
                    Back to Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className={cardBgClass} data-testid="bills-stat-card">
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm ${mutedTextClass}`}>Bills</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${textClass}`}>{(hideDuplicates ? stats.unique_bills : stats.total_bills)?.toLocaleString() || 0}</div>
              {stats.duplicate_bills > 0 && (
                <div className={`text-xs ${mutedTextClass}`}>
                  {hideDuplicates ? `${stats.duplicate_bills} duplicates hidden` : `${stats.unique_bills} unique`}
                </div>
              )}
            </CardContent>
          </Card>
          <Card className={cardBgClass} data-testid="payments-stat-card">
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm ${mutedTextClass}`}>Payments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${textClass}`}>{(hideDuplicates ? stats.unique_payments : stats.total_payments)?.toLocaleString() || 0}</div>
              {stats.duplicate_payments > 0 && (
                <div className={`text-xs ${mutedTextClass}`}>
                  {hideDuplicates ? `${stats.duplicate_payments} duplicates hidden` : `${stats.unique_payments} unique`}
                </div>
              )}
            </CardContent>
          </Card>
          <Card className={cardBgClass} data-testid="reports-stat-card">
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm ${mutedTextClass}`}>Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${textClass}`}>{(hideDuplicates ? stats.unique_reports : stats.total_reports)?.toLocaleString() || 0}</div>
              {stats.duplicate_reports > 0 && (
                <div className={`text-xs ${mutedTextClass}`}>
                  {hideDuplicates ? `${stats.duplicate_reports} duplicates hidden` : `${stats.unique_reports} unique`}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4">
          <div className={`flex items-center gap-1 p-1 rounded-lg ${preferences.theme === 'light' ? 'bg-gray-100' : 'bg-zinc-900'}`}>
            <Button
              variant={activeTab === "bills" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("bills")}
              className={activeTab === "bills" ? "bg-purple-600 text-white font-semibold" : mutedTextClass}
              data-testid="tab-bills"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Bills
            </Button>
            <Button
              variant={activeTab === "payments" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("payments")}
              className={activeTab === "payments" ? "bg-purple-600 text-white font-semibold" : mutedTextClass}
              data-testid="tab-payments"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Payments
            </Button>
            <Button
              variant={activeTab === "reports" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("reports")}
              className={activeTab === "reports" ? "bg-purple-600 text-white font-semibold" : mutedTextClass}
              data-testid="tab-reports"
            >
              <FileText className="w-4 h-4 mr-2" />
              Reports
            </Button>
          </div>

          <div className="flex-1" />

          {/* Duplicate Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHideDuplicates(!hideDuplicates)}
            className={`h-8 text-xs ${hideDuplicates ? '' : 'bg-amber-900/30 text-amber-400 border-amber-900/50'}`}
          >
            {hideDuplicates ? "Unique" : "All"}
          </Button>

          <Button variant="outline" size="sm" onClick={() => {
            if (activeTab === "bills") loadBills();
            else if (activeTab === "payments") loadPayments();
            else loadReports();
          }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Bills Table */}
        {activeTab === "bills" && (
          <Card className={cardBgClass} data-testid="bills-table">
            <CardHeader className="py-3 border-b border-zinc-800">
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  placeholder="Filter vendor..."
                  value={billFilters.vendor}
                  onChange={(e) => { setBillFilters({...billFilters, vendor: e.target.value}); setBillPage(1); }}
                  className="w-40 h-8 text-sm"
                />
                <Input
                  placeholder="Filter billing ID..."
                  value={billFilters.billing_id}
                  onChange={(e) => { setBillFilters({...billFilters, billing_id: e.target.value}); setBillPage(1); }}
                  className="w-40 h-8 text-sm"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={tableBgClass}>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>Vendor</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>Billing ID</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>Invoice Date</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>Start Date</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>End Date</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass} text-right`}>Charges</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass} text-right`}>Amount Due</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass} text-center`}>PDF</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bills.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12">
                          <Receipt className="w-12 h-12 mx-auto mb-3 opacity-20" />
                          <p className={mutedTextClass}>No bills found</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      bills.map((bill, idx) => (
                        <TableRow key={bill.id || idx} className={`border-b ${borderClass} ${rowHoverClass}`}>
                          <TableCell className={`font-medium text-sm ${rowTextClass}`}>{bill.vendor}</TableCell>
                          <TableCell className={`font-mono text-xs ${rowTextClass}`}>{bill.billing_id}</TableCell>
                          <TableCell className={`text-sm ${rowTextClass}`}>{bill.invoice_date}</TableCell>
                          <TableCell className={`text-sm ${rowTextClass}`}>{bill.billing_start_date}</TableCell>
                          <TableCell className={`text-sm ${rowTextClass}`}>{bill.billing_end_date}</TableCell>
                          <TableCell className={`text-right text-sm ${rowTextClass}`}>{bill.current_charges}</TableCell>
                          <TableCell className={`text-right font-medium text-sm ${rowTextClass}`}>{bill.total_amount_due}</TableCell>
                          <TableCell className="text-center">
                            {bill.s3_url ? (
                              <Button variant="ghost" size="sm" onClick={() => downloadFile(bill.s3_url)} className={downloadBtnClass}>
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className={mutedTextClass}>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination */}
              {billPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-zinc-800">
                  <div className={`text-sm ${mutedTextClass}`}>Page {billPage} of {billPages}</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={billPage <= 1} onClick={() => setBillPage(billPage - 1)}>
                      Previous
                    </Button>
                    <Button size="sm" variant="outline" disabled={billPage >= billPages} onClick={() => setBillPage(billPage + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payments Table */}
        {activeTab === "payments" && (
          <Card className={cardBgClass} data-testid="payments-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={tableBgClass}>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>Date</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>Qty of Bills</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass} text-right`}>Amount Due</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>File Name</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass} text-center`}>Download</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12">
                          <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-20" />
                          <p className={mutedTextClass}>No payments found</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((payment, idx) => (
                        <TableRow key={payment.id || idx} className={`border-b ${borderClass} ${rowHoverClass}`}>
                          <TableCell className={`text-sm ${rowTextClass}`}>{payment.date}</TableCell>
                          <TableCell className={`text-sm ${rowTextClass}`}>{payment.qty_of_bills}</TableCell>
                          <TableCell className={`text-right font-medium text-sm ${rowTextClass}`}>{payment.amount_due}</TableCell>
                          <TableCell className={`text-sm font-mono truncate max-w-[200px] ${rowTextClass}`}>{payment.file_name}</TableCell>
                          <TableCell className="text-center">
                            {(payment.additional_files?.length > 0) ? (
                              <select
                                className={`${preferences.theme === 'light' ? 'bg-gray-100 border-gray-300 text-gray-700' : 'bg-zinc-800 border-zinc-700 text-zinc-300'} border rounded text-xs py-1 px-2 max-w-[140px]`}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    downloadFile(e.target.value);
                                    e.target.value = '';
                                  }
                                }}
                                defaultValue=""
                                data-testid={`payment-download-${idx}`}
                              >
                                <option value="">Download ({(payment.additional_files?.length || 0) + (payment.s3_url ? 1 : 0)})</option>
                                {payment.s3_url && (
                                  <option value={payment.s3_url}>
                                    {(payment.file_name || "Main File").substring(0, 30)}
                                  </option>
                                )}
                                {payment.additional_files.map((file, fIdx) => (
                                  <option key={fIdx} value={file.s3_url || file.url}>
                                    {(file.file_name || `File ${fIdx + 1}`).substring(0, 30)}
                                  </option>
                                ))}
                              </select>
                            ) : payment.s3_url ? (
                              <Button variant="ghost" size="sm" onClick={() => downloadFile(payment.s3_url)} className={downloadBtnClass}>
                                <Download className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className={mutedTextClass}>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination */}
              {paymentPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-zinc-800">
                  <div className={`text-sm ${mutedTextClass}`}>Page {paymentPage} of {paymentPages}</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={paymentPage <= 1} onClick={() => setPaymentPage(paymentPage - 1)}>
                      Previous
                    </Button>
                    <Button size="sm" variant="outline" disabled={paymentPage >= paymentPages} onClick={() => setPaymentPage(paymentPage + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Reports Table */}
        {activeTab === "reports" && (
          <Card className={cardBgClass} data-testid="reports-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={tableBgClass}>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>Report Name</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>Date</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>Status</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass}`}>File</TableHead>
                      <TableHead className={`text-xs uppercase ${mutedTextClass} text-center`}>Download</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12">
                          <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
                          <p className={mutedTextClass}>No reports found</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reports.map((report, idx) => (
                        <TableRow key={report.id || idx} className={`border-b ${borderClass} ${rowHoverClass}`}>
                          <TableCell className={`text-sm font-medium max-w-[200px] truncate ${rowTextClass}`}>{report.report_name}</TableCell>
                          <TableCell className={`text-sm ${rowTextClass}`}>{report.report_date}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${statusBadgeClass}`}>
                              {report.status}
                            </span>
                          </TableCell>
                          <TableCell className={`text-sm font-mono truncate max-w-[200px] ${rowTextClass}`}>{report.file_name}</TableCell>
                          <TableCell className="text-center">
                            {report.s3_url ? (
                              <Button variant="ghost" size="sm" onClick={() => downloadFile(report.s3_url)} className={downloadBtnClass}>
                                <Download className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className={mutedTextClass}>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination */}
              {reportPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-zinc-800">
                  <div className={`text-sm ${mutedTextClass}`}>Page {reportPage} of {reportPages}</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={reportPage <= 1} onClick={() => setReportPage(reportPage - 1)}>
                      Previous
                    </Button>
                    <Button size="sm" variant="outline" disabled={reportPage >= reportPages} onClick={() => setReportPage(reportPage + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
